import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { showToast } from '../../utils';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import { selectCurrentUser } from '../user/user-slice';
import {
  DueDatesSettings,
  DueDatesSettingsData,
  EngineeringSettings,
  EngineeringSettingsData,
  NotificationsSettings,
  NotificationsSettingsData,
  PaintConfigSettings,
  PaintConfigSettingsData,
} from './components';
import {
  useGetDueDatesSettingsQuery,
  useGetEngineeringFeesSettingsQuery,
  useGetNotificationsSettingsQuery,
  useGetPaintSettingsQuery,
  useUpdateDueDatesSettingsMutation,
  useUpdateEngineeringFeesSettingsMutation,
  useUpdateNotificationsSettingsMutation,
  useUpdatePaintSettingsMutation,
} from './system-settings-api-slice';

// Helper function to extract error message from API errors
const getErrorMessage = (error: unknown, defaultMessage: string): string => {
  if (error && typeof error === 'object' && 'data' in error) {
    const errorData = error.data;
    if (
      errorData &&
      typeof errorData === 'object' &&
      'message' in errorData &&
      typeof errorData.message === 'string'
    ) {
      return errorData.message;
    }
  }
  return defaultMessage;
};

// Tab interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser);
  const [tabValue, setTabValue] = useState(0);
  const [selectedManufacturer, setSelectedManufacturer] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});

  // Note: manufacturerId is determined in apiManufacturerId below

  // Fetch manufacturers for admin users
  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: currentUser?.role !== UserRole.ADMINISTRATOR,
  });

  // API Queries - CSR and Dealer users can read but not write
  const canReadSettings =
    currentUser?.role === UserRole.ADMINISTRATOR ||
    currentUser?.role === UserRole.SUPERVISOR ||
    currentUser?.role === UserRole.CSR ||
    currentUser?.role === UserRole.DEALER;
  const canWriteSettings =
    currentUser?.role === UserRole.ADMINISTRATOR || currentUser?.role === UserRole.SUPERVISOR;
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const isCSR = currentUser?.role === UserRole.CSR;
  const isDealer = currentUser?.role === UserRole.DEALER;

  // For Admin users: pass manufacturerId only if selected
  // For Supervisor/CSR users: pass undefined to use bearer token
  const apiManufacturerId = isAdmin ? selectedManufacturer || undefined : undefined;

  const {
    data: dueDatesData,
    isLoading: dueDatesLoading,
    refetch: refetchDueDates,
  } = useGetDueDatesSettingsQuery(apiManufacturerId, {
    skip: !canReadSettings || (isAdmin && !selectedManufacturer),
    refetchOnMountOrArgChange: true,
  });

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    refetch: refetchNotifications,
  } = useGetNotificationsSettingsQuery(apiManufacturerId, {
    skip: !canReadSettings || (isAdmin && !selectedManufacturer),
    refetchOnMountOrArgChange: true,
  });

  const {
    data: engineeringData,
    isLoading: engineeringLoading,
    refetch: refetchEngineering,
  } = useGetEngineeringFeesSettingsQuery(apiManufacturerId, {
    skip: !canReadSettings || (isAdmin && !selectedManufacturer),
    refetchOnMountOrArgChange: true,
  });

  const {
    data: paintData,
    isLoading: paintLoading,
    refetch: refetchPaint,
  } = useGetPaintSettingsQuery(apiManufacturerId, {
    skip: !canReadSettings || (isAdmin && !selectedManufacturer),
    refetchOnMountOrArgChange: true,
  });

  // API Mutations
  const [updateDueDates] = useUpdateDueDatesSettingsMutation();
  const [updateNotifications] = useUpdateNotificationsSettingsMutation();
  const [updateEngineeringFees] = useUpdateEngineeringFeesSettingsMutation();
  const [updatePaintSettings] = useUpdatePaintSettingsMutation();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleManufacturerChange = (event: SelectChangeEvent<number>) => {
    setSelectedManufacturer(event.target.value as number);
    // Reset to Due Dates tab when manufacturer changes
    setTabValue(0);
    // Note: Manufacturer selection doesn't affect unsaved changes for individual tabs
  };

  // API Save Handlers
  const handleDueDatesSave = async (data: DueDatesSettingsData) => {
    if (!canWriteSettings) {
      showToast('You do not have permission to modify settings', 'error');
      return;
    }

    setIsLoading(prev => ({ ...prev, dueDates: true }));

    try {
      await updateDueDates({
        manufacturerId: apiManufacturerId,
        dueDates: {
          leadTime: data.leadTime,
          deliveryWindow: data.deliveryWindow,
          maxLinearFeet: data.maxLinealFeet,
        },
      }).unwrap();

      // Refetch to ensure fresh data
      await refetchDueDates();

      showToast('Due dates settings saved successfully', 'success');
    } catch (error: unknown) {
      console.error('Error saving due dates settings:', error);
      showToast(getErrorMessage(error, 'Failed to save due dates settings'), 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, dueDates: false }));
    }
  };

  const handleNotificationsSave = async (data: NotificationsSettingsData) => {
    if (!canWriteSettings) {
      showToast('You do not have permission to modify settings', 'error');
      return;
    }

    setIsLoading(prev => ({ ...prev, notifications: true }));

    try {
      await updateNotifications({
        manufacturerId: apiManufacturerId,
        notifications: {
          emailList: data.emailList,
        },
      }).unwrap();

      // Refetch to ensure fresh data
      await refetchNotifications();

      showToast('Notifications settings saved successfully', 'success');
    } catch (error: unknown) {
      console.error('Error saving notifications settings:', error);
      showToast(getErrorMessage(error, 'Failed to save notifications settings'), 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, notifications: false }));
    }
  };

  const handleEngineeringSave = async (data: EngineeringSettingsData) => {
    if (!canWriteSettings) {
      showToast('You do not have permission to modify settings', 'error');
      return;
    }

    setIsLoading(prev => ({ ...prev, engineering: true }));

    try {
      await updateEngineeringFees({
        manufacturerId: apiManufacturerId,
        engineeringFees: {
          openPoleBarnFee: data.openPoleBarnFee,
          partialClosedPoleBarnFee: data.partialClosedPoleBarnFee,
          fullyEnclosedPoleBarnFee: data.fullyEnclosedPoleBarnFee,
          permanentDwellingFee: data.permanentDwellingNoMepFee,
        },
      }).unwrap();

      // Refetch to ensure fresh data
      await refetchEngineering();

      showToast('Engineering settings saved successfully', 'success');
    } catch (error: unknown) {
      console.error('Error saving engineering settings:', error);
      showToast(getErrorMessage(error, 'Failed to save engineering settings'), 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, engineering: false }));
    }
  };

  const handlePaintConfigSave = async (data: PaintConfigSettingsData) => {
    if (!canWriteSettings) {
      showToast('You do not have permission to modify settings', 'error');
      return;
    }

    setIsLoading(prev => ({ ...prev, paintConfig: true }));

    try {
      await updatePaintSettings({
        manufacturerId: apiManufacturerId,
        paintSettings: {
          surfaceAreaPerCoveredPerGallon: data.wetPaintSettings.surfaceAreaPerGallonSqIn,
          surfaceAreaPerLinearFoot: data.wetPaintSettings.surfaceAreaPerLinearFoot,
          markupPercentage: data.wetPaintSettings.markupPercentage,
          workHoursPerMonth: data.wetPaintSettings.workHoursPerMonth,
          utilityCostList: data.utilityCosts,
          packagingCostList: data.packagingCosts,
        },
      }).unwrap();

      // Refetch to ensure fresh data
      await refetchPaint();

      showToast('Paint configuration settings saved successfully', 'success');
    } catch (error: unknown) {
      console.error('Error saving paint config settings:', error);
      showToast(getErrorMessage(error, 'Failed to save paint configuration settings'), 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, paintConfig: false }));
    }
  };

  // Show message if no manufacturer is selected for admin users (but still show the dropdown)
  const showManufacturerSelectionMessage = isAdmin && !selectedManufacturer;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            System Settings
          </Typography>
          {(isCSR || isDealer) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {isCSR
                ? 'Customer Service Representatives have read-only access to System Settings. You can view the settings but cannot make changes.'
                : 'Dealers have read-only access to System Settings. You can view the settings but cannot make changes.'}
            </Alert>
          )}
        </Box>

        {isAdmin && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Please select the Manufacturer:
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={selectedManufacturer}
                onChange={handleManufacturerChange}
                label="Manufacturer"
              >
                {manufacturers.map(manufacturer => (
                  <MenuItem key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.licenseeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Manufacturer Selection Message for Admin Users */}
      {showManufacturerSelectionMessage && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info">
            Please select a manufacturer to view and manage system settings.
          </Alert>
        </Box>
      )}

      {/* Tabs Section */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="fullWidth"
          >
            <Tab label="Due Dates" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
            <Tab label="Notifications" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
            <Tab label="Engineering" id="settings-tab-2" aria-controls="settings-tabpanel-2" />
            <Tab
              label="Paint Configurations"
              id="settings-tab-3"
              aria-controls="settings-tabpanel-3"
            />
          </Tabs>
        </Box>

        {/* Due Dates Tab */}
        <TabPanel value={tabValue} index={0}>
          {showManufacturerSelectionMessage ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Please select a manufacturer from the dropdown above to view and manage due dates
                settings.
              </Typography>
            </Box>
          ) : dueDatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading due dates settings...
              </Typography>
            </Box>
          ) : (
            <DueDatesSettings
              initialData={
                dueDatesData
                  ? {
                      leadTime: dueDatesData.leadTime,
                      deliveryWindow: dueDatesData.deliveryWindow,
                      maxLinealFeet: dueDatesData.maxLinearFeet,
                    }
                  : undefined
              }
              onSave={handleDueDatesSave}
              isSaving={isLoading.dueDates}
              readOnly={!canWriteSettings}
            />
          )}
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={1}>
          {showManufacturerSelectionMessage ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Please select a manufacturer from the dropdown above to view and manage notification
                settings.
              </Typography>
            </Box>
          ) : notificationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading notifications settings...
              </Typography>
            </Box>
          ) : (
            <NotificationsSettings
              initialData={
                notificationsData
                  ? {
                      emailList: notificationsData.emailList,
                    }
                  : undefined
              }
              onSave={handleNotificationsSave}
              isSaving={isLoading.notifications}
              readOnly={!canWriteSettings}
            />
          )}
        </TabPanel>

        {/* Engineering Tab */}
        <TabPanel value={tabValue} index={2}>
          {showManufacturerSelectionMessage ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Please select a manufacturer from the dropdown above to view and manage engineering
                settings.
              </Typography>
            </Box>
          ) : engineeringLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading engineering settings...
              </Typography>
            </Box>
          ) : (
            <EngineeringSettings
              initialData={
                engineeringData
                  ? {
                      openPoleBarnFee: engineeringData.openPoleBarnFee,
                      partialClosedPoleBarnFee: engineeringData.partialClosedPoleBarnFee,
                      fullyEnclosedPoleBarnFee: engineeringData.fullyEnclosedPoleBarnFee,
                      permanentDwellingNoMepFee: engineeringData.permanentDwellingFee,
                    }
                  : undefined
              }
              onSave={handleEngineeringSave}
              isSaving={isLoading.engineering}
              readOnly={!canWriteSettings}
            />
          )}
        </TabPanel>

        {/* Paint Configurations Tab */}
        <TabPanel value={tabValue} index={3}>
          {showManufacturerSelectionMessage ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Please select a manufacturer from the dropdown above to view and manage paint
                configuration settings.
              </Typography>
            </Box>
          ) : paintLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading paint configuration settings...
              </Typography>
            </Box>
          ) : (
            <PaintConfigSettings
              initialData={
                paintData
                  ? {
                      wetPaintSettings: {
                        surfaceAreaPerGallonSqIn: paintData.surfaceAreaPerCoveredPerGallon,
                        surfaceAreaPerLinearFoot: paintData.surfaceAreaPerLinearFoot,
                        markupPercentage: paintData.markupPercentage,
                        workHoursPerMonth: paintData.workHoursPerMonth,
                      },
                      utilityCosts: paintData.utilityCostList,
                      packagingCosts: paintData.packagingCostList,
                    }
                  : undefined
              }
              onSave={handlePaintConfigSave}
              isSaving={isLoading.paintConfig}
              readOnly={!canWriteSettings}
            />
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
